import Decimal from 'decimal.js';
import { divide } from 'lodash-es';
import { toCamelCase } from '@raft/utils/src/case/camel-case';

export interface IssueVersion {
  version: string;
  count: number;
  deviceCount: number;
}
export enum ByteUnit {
  B='B',
  KB='KB',
  MB='MB',
  GB='GB'
}

// 格式化特征堆栈KeyMethods以','切割
export const formatKeyMethodsByComma = (keyMethods?: string) => {
  if (!keyMethods || keyMethods === '') {
    return [];
  }
  if (keyMethods === 'UnClassify') {
    return ['UnClassify'];
  }
  return keyMethods.split(',');
};

// 格式化特征堆栈KeyMethods以'\n'切割
export const formatKeyMethodsByLine = (keyMethods?: string | null) => {
  if (!keyMethods || keyMethods === '') {
    return [];
  }
  if (keyMethods === 'UnClassify') {
    return ['UnClassify'];
  }

  return keyMethods?.split('\n')?.filter(item => !!item) ?? [];
};

// 格式化键值对形式的特征堆栈KeyMethods
export const formatKeyMethodsByParse = (keyMethods?: string) => {
  if (!keyMethods || keyMethods === '') {
    return [];
  }

  try {
    return JSON.parse(keyMethods);
  } catch (e) {
    // 如果json parse失败的，统一当字符串直接展示
    return [keyMethods];
  }
};

// 格式化键值对形式的特征堆栈KeyMethods
export const formatKeyMethodsByKeyValue = (keyMethods?: string): string[] => {
  if (!keyMethods || keyMethods === '') {
    return [];
  }

  try {
    return JSON.parse(keyMethods).map((item: {
      Cost: number;
      Method: string;
    }) => item.Method);
  } catch (e) {
    // 如果json parse失败的，统一当字符串直接展示
    return [keyMethods];
  }
};

// 格式化json格式的特征堆栈KeyMethods
export const formatKeyMethodsByJson = (keyMethods: string) => {
  try {
    return JSON.parse(keyMethods);
  } catch (e) {
    return [];
  }
};

export type RetracedData = {
  UUID: string;
  fileName: string;
  code: string;
};

export const formatRetracedDetail = (retracedDetail: string): RetracedData[]  => {
  const data: RetracedData[] = [];
  retracedDetail.split(';').filter(retraced => !!retraced)
    .map((retraced: string) => retraced.split(','))
    .forEach(item => data.push({
      UUID: item[0].replace(/-/g, ''),
      fileName: item[1],
      code: item[2],
    }));
  return data;
};

export const formatOriginalStack = (originalStack: string) => {
  try {
    return JSON.parse(originalStack).map((item: any) => toCamelCase(item));
  } catch (e) {
    return [];
  }
};

export const formatMemorySize = (size: number) => {
  const GB = 1024;
  const minusGB = -1024;
  if (size >= GB) {
    return `${divide(size, GB)?.toFixed(2)} GB`;
  }
  if (size <= minusGB) {
    return `-${divide(size, minusGB)?.toFixed(2)} GB`;
  }
  return `${size.toFixed(2)} MB`;
};

export const formatAllMallocSize = (size: number) => {
  if (size > 0) {
    return `${size.toFixed(2)} GB`;
  }
  return '0 GB';
};

export const formatByte = (size: number | null | undefined) => {
  if (Number.isNaN(size) || size === null || size === undefined) {
    return '0 B';
  }
  if (size >= 1024 * 1024 * 1024) {
    return `${(size / (1024 * 1024 * 1024)).toFixed(2)} GB`;
  } if (size >= 1024 * 1024) {
    return `${(size / (1024 * 1024)).toFixed(2)} MB`;
  } if (size >= 1024) {
    return `${(size / 1024).toFixed(2)} KB`;
  }
  return `${size.toFixed(2)} B`;
};

export const formatKeyMethodsByArray = (keyMethods: string[]) => keyMethods.join('\n')
  .replaceAll('\r', ' ');

export const bytesToKb = (b: number) => {
  if (b === 0) return 0;

  if (isNaN(b) || !b) {
    return null;
  }
  return b / 1024;
};

export const kbToBytes = (kb: number) => {
  if (kb === 0) return 0;

  if (isNaN(kb) || !kb) {
    return null;
  }
  return kb * 1024;
};


export const formatByteSize = (size: number | null | undefined, originalUnit: ByteUnit) => {
  // @ts-ignore
  if (Number.isNaN(size) || size === null || size === undefined || size === 'NaN') {
    return size;
  }
  if (originalUnit === ByteUnit.GB) {
    return `${size.toFixed(2)} ${ByteUnit.GB}`;
  }

  if (originalUnit === ByteUnit.MB) {
    if (size >= 1024) {
      return `${(size / 1024).toFixed(2)} ${ByteUnit.GB}`;
    }
    return `${size.toFixed(2)} ${ByteUnit.MB}`;
  }
  if (originalUnit === ByteUnit.KB) {
    if (size >= 1024 * 1024) {
      return `${(size / (1024 * 1024)).toFixed(2)} ${ByteUnit.GB}`;
    }
    if (size >= 1024) {
      return `${(size / 1024).toFixed(2)} ${ByteUnit.MB}`;
    }
    return `${size.toFixed(2)} ${ByteUnit.KB}`;
  }
  if (originalUnit === ByteUnit.B) {
    if (size >= 1024 * 1024 * 1024) {
      return `${(size / (1024 * 1024 * 1024)).toFixed(2)} ${ByteUnit.GB}`;
    }
    if (size >= 1024 * 1024) {
      return `${(size / (1024 * 1024)).toFixed(2)} ${ByteUnit.MB}`;
    }
    if (size >= 1024) {
      return `${(size / 1024).toFixed(2)} ${ByteUnit.KB}`;
    }
    return `${size.toFixed(2)} ${ByteUnit.KB}`;
  }

  return `${size.toFixed(2)} ${ByteUnit.B}`;
};

export const formatProductId = (value: string) => {
  const regex = /\(([^)]+)\)/;
  const match = regex.exec(value);
  if (match) {
    return match[1];
  }
  return value;
};

// 额度单位
export const formatQuotaUnit = (quota: number) => {
  if (quota >= 1000000) {
    return `${new Decimal(quota).dividedBy(100000000)
      .toDecimalPlaces(2, Decimal.ROUND_DOWN)
      .toString()}亿`;
  }
  if (quota >= 100) {
    return `${new Decimal(quota).dividedBy(10000)
      .toDecimalPlaces(2, Decimal.ROUND_DOWN)
      .toString()}万`;
  }
  return quota;
};
